import { defaultContent, MEDIA_KIT_CONTENT_VERSION } from "./defaultContent";
import { normalizeLayout } from "./layout";
import { constrainToMediaKitFolder } from "./mediaKitFolder";
import type { AudienceRow, CloudinaryImage, MediaKitContent, Metric, MetricTone } from "./types";

/**
 * Performance / reach KPIs that should read as “positive” (formal sage green).
 * Platform identity labels stay champagne (gold).
 */
const POSITIVE_METRIC_IDS = new Set([
  "engagement",
  "avg-views",
  "max-views",
  "campaigns",
  "ig-avg",
  "ig-best",
  "tt-avg",
  "tt-best",
]);

/** Follower counts = inventory (champagne), not “performance green” */
const PLATFORM_LABEL_IDS = new Set(["ig-followers", "tt-followers"]);

/** Old CMS stored platform name as value ("Instagram") instead of the number. */
function isLegacyPlatformMetrics(metrics: Metric[] | undefined): boolean {
  if (!metrics?.length) return true;
  return metrics.some((m) => /^(instagram|tiktok)$/i.test(String(m.value).trim()));
}

function coerceTone(raw: unknown, fallback: MetricTone): MetricTone {
  if (raw === "gold" || raw === "red" || raw === "green") return raw;
  return fallback;
}

function formalizePerformanceMetric(metric: Metric): Metric {
  if (POSITIVE_METRIC_IDS.has(metric.id)) {
    return { ...metric, tone: "green" };
  }
  if (PLATFORM_LABEL_IDS.has(metric.id)) {
    return { ...metric, tone: "gold" };
  }
  // Legacy CMS used “red” as hot accent for good numbers — map to green
  if (metric.tone === "red") {
    return { ...metric, tone: "green" };
  }
  return { ...metric, tone: coerceTone(metric.tone, "gold") };
}

function formalizeAudienceRow(row: AudienceRow, isPrimary: boolean): AudienceRow {
  return {
    ...row,
    tone: isPrimary ? "green" : "gold",
  };
}

function formalizeGender(metric: Metric): Metric {
  // Demographics are structural, not “bad” — champagne hierarchy, no neon red
  return { ...metric, tone: "gold" };
}

/**
 * Force every image onto `{brand}/mediakit`. CMS/legacy portfolio URLs
 * fall back to brand defaults so the public page never leaves that folder.
 */
export function constrainMediaKitImages(content: MediaKitContent): MediaKitContent {
  const base = defaultContent;
  const img = (value: CloudinaryImage | undefined, fallback: CloudinaryImage) =>
    constrainToMediaKitFolder(value ?? fallback, fallback);

  return {
    ...content,
    hero: {
      ...content.hero,
      image: img(content.hero.image, base.hero.image),
    },
    about: {
      ...content.about,
      image: img(content.about.image, base.about.image),
    },
    visualWorld: {
      ...content.visualWorld,
      images: content.visualWorld.images.map((item, i) => {
        const fb = base.visualWorld.images[i] ?? base.visualWorld.images[0];
        const constrained = img(item, fb);
        return { ...item, ...constrained, id: item.id, caption: item.caption, sortOrder: item.sortOrder };
      }),
    },
    campaigns: {
      ...content.campaigns,
      concepts: content.campaigns.concepts.map((concept, i) => {
        const fb = base.campaigns.concepts[i]?.image ?? base.campaigns.concepts[0].image;
        return {
          ...concept,
          image: img(concept.image, fb),
        };
      }),
    },
  };
}

/**
 * Apply formal black media-kit tone system on top of CMS/default data so
 * published JSONB with legacy red accents still renders coherently.
 */
export function formalizeMediaKitTones(content: MediaKitContent): MediaKitContent {
  const headlineMetrics = content.performance.headlineMetrics.map(formalizePerformanceMetric);
  const platformMetrics = content.performance.platformMetrics.map(formalizePerformanceMetric);

  const ageSorted = [...content.audience.age].sort((a, b) => b.value - a.value);
  const primaryAgeId = ageSorted[0]?.id;
  const countrySorted = [...content.audience.countries].sort((a, b) => b.value - a.value);
  const primaryCountryId = countrySorted[0]?.id;

  const toned: MediaKitContent = {
    ...content,
    performance: {
      ...content.performance,
      headlineMetrics,
      platformMetrics,
    },
    audience: {
      ...content.audience,
      age: content.audience.age.map((row) =>
        formalizeAudienceRow(row, row.id === primaryAgeId),
      ),
      countries: content.audience.countries.map((row) =>
        formalizeAudienceRow(row, row.id === primaryCountryId),
      ),
      gender: content.audience.gender.map(formalizeGender),
    },
  };

  return constrainMediaKitImages(toned);
}

/**
 * Deep-merge CMS payload onto brand defaults so partial/legacy JSONB
 * never blanks required arrays/objects on the public page.
 */
export function mergeMediaKitContent(
  partial: Partial<MediaKitContent> | null | undefined,
): MediaKitContent {
  if (!partial || typeof partial !== "object") {
    return formalizeMediaKitTones(structuredClone(defaultContent));
  }

  const base = structuredClone(defaultContent);
  // CMS before current framing version is enriched from brand defaults (public page)
  const legacyKit =
    !partial.version || Number(partial.version) < MEDIA_KIT_CONTENT_VERSION;

  const cmsPlatform = partial.performance?.platformMetrics;
  const platformMetrics =
    !legacyKit &&
    Array.isArray(cmsPlatform) &&
    cmsPlatform.length &&
    !isLegacyPlatformMetrics(cmsPlatform)
      ? cmsPlatform
      : base.performance.platformMetrics;

  const cmsHeadline = partial.performance?.headlineMetrics;
  const headlineMetrics =
    !legacyKit && Array.isArray(cmsHeadline) && cmsHeadline.length
      ? cmsHeadline
      : base.performance.headlineMetrics;

  const merged: MediaKitContent = {
    ...base,
    ...partial,
    version: Math.max(Number(partial.version) || 0, base.version),
    layout: normalizeLayout(
      (partial as Partial<MediaKitContent>).layout ?? base.layout,
    ),
    brand: { ...base.brand, ...(partial.brand ?? {}) },
    profile: {
      ...base.profile,
      ...(partial.profile ?? {}),
      // Prefer brand-hiring descriptor until CMS is republished at v2+
      descriptor: legacyKit
        ? base.profile.descriptor
        : (partial.profile?.descriptor ?? base.profile.descriptor),
      bio: legacyKit ? base.profile.bio : (partial.profile?.bio ?? base.profile.bio),
      location: legacyKit
        ? base.profile.location
        : (partial.profile?.location ?? base.profile.location),
      instagram: {
        ...base.profile.instagram,
        ...(partial.profile?.instagram ?? {}),
      },
      email: {
        ...base.profile.email,
        ...(partial.profile?.email ?? {}),
        href: legacyKit
          ? base.profile.email.href
          : (partial.profile?.email?.href ?? base.profile.email.href),
      },
    },
    hero: {
      ...base.hero,
      ...(partial.hero ?? {}),
      kicker: legacyKit ? base.hero.kicker : (partial.hero?.kicker ?? base.hero.kicker),
      image: {
        ...base.hero.image,
        ...(partial.hero?.image ?? {}),
      },
    },
    about: {
      ...base.about,
      ...(legacyKit ? {} : (partial.about ?? {})),
      ...(legacyKit
        ? {
            eyebrow: base.about.eyebrow,
            heading: base.about.heading,
            headingAccent: base.about.headingAccent,
            paragraphs: base.about.paragraphs,
            tags: base.about.tags,
          }
        : {
            paragraphs:
              Array.isArray(partial.about?.paragraphs) && partial.about.paragraphs.length
                ? partial.about.paragraphs
                : base.about.paragraphs,
            tags:
              Array.isArray(partial.about?.tags) && partial.about.tags.length
                ? partial.about.tags
                : base.about.tags,
          }),
      image: {
        ...base.about.image,
        ...(partial.about?.image ?? {}),
      },
    },
    performance: {
      ...base.performance,
      ...(legacyKit ? {} : (partial.performance ?? {})),
      periodLabel: base.performance.periodLabel,
      headlineMetrics,
      platformMetrics,
      noteTitle: legacyKit
        ? base.performance.noteTitle
        : (partial.performance?.noteTitle ?? base.performance.noteTitle),
      noteBody: legacyKit
        ? base.performance.noteBody
        : (partial.performance?.noteBody ?? base.performance.noteBody),
      eyebrow: legacyKit
        ? base.performance.eyebrow
        : (partial.performance?.eyebrow ?? base.performance.eyebrow),
      heading: legacyKit
        ? base.performance.heading
        : (partial.performance?.heading ?? base.performance.heading),
      headingAccent: legacyKit
        ? base.performance.headingAccent
        : (partial.performance?.headingAccent ?? base.performance.headingAccent),
    },
    audience: {
      ...base.audience,
      ...(legacyKit ? {} : (partial.audience ?? {})),
      intro: legacyKit
        ? base.audience.intro
        : (partial.audience?.intro ?? base.audience.intro),
      eyebrow: legacyKit
        ? base.audience.eyebrow
        : (partial.audience?.eyebrow ?? base.audience.eyebrow),
      heading: legacyKit
        ? base.audience.heading
        : (partial.audience?.heading ?? base.audience.heading),
      headingAccent: legacyKit
        ? base.audience.headingAccent
        : (partial.audience?.headingAccent ?? base.audience.headingAccent),
      age:
        Array.isArray(partial.audience?.age) && partial.audience.age.length
          ? partial.audience.age
          : base.audience.age,
      countries:
        Array.isArray(partial.audience?.countries) &&
        partial.audience.countries.length
          ? partial.audience.countries
          : base.audience.countries,
      gender:
        Array.isArray(partial.audience?.gender) && partial.audience.gender.length
          ? partial.audience.gender
          : base.audience.gender,
      interests:
        Array.isArray(partial.audience?.interests) &&
        partial.audience.interests.length &&
        !legacyKit
          ? partial.audience.interests
          : base.audience.interests,
    },
    visualWorld: {
      ...base.visualWorld,
      ...(partial.visualWorld ?? {}),
      images:
        Array.isArray(partial.visualWorld?.images) &&
        partial.visualWorld.images.length
          ? partial.visualWorld.images
          : base.visualWorld.images,
    },
    campaigns: {
      ...base.campaigns,
      ...(legacyKit ? {} : (partial.campaigns ?? {})),
      intro: legacyKit
        ? base.campaigns.intro
        : (partial.campaigns?.intro ?? base.campaigns.intro),
      eyebrow: legacyKit
        ? base.campaigns.eyebrow
        : (partial.campaigns?.eyebrow ?? base.campaigns.eyebrow),
      heading: legacyKit
        ? base.campaigns.heading
        : (partial.campaigns?.heading ?? base.campaigns.heading),
      headingAccent: legacyKit
        ? base.campaigns.headingAccent
        : (partial.campaigns?.headingAccent ?? base.campaigns.headingAccent),
      concepts:
        !legacyKit &&
        Array.isArray(partial.campaigns?.concepts) &&
        partial.campaigns.concepts.length
          ? partial.campaigns.concepts
          : base.campaigns.concepts,
    },
    collaboration: {
      ...base.collaboration,
      ...(legacyKit ? {} : ((partial as MediaKitContent).collaboration ?? {})),
      formats:
        !legacyKit &&
        Array.isArray((partial as MediaKitContent).collaboration?.formats) &&
        (partial as MediaKitContent).collaboration!.formats.length
          ? (partial as MediaKitContent).collaboration!.formats
          : base.collaboration.formats,
      notes:
        !legacyKit &&
        Array.isArray((partial as MediaKitContent).collaboration?.notes) &&
        (partial as MediaKitContent).collaboration!.notes.length
          ? (partial as MediaKitContent).collaboration!.notes
          : base.collaboration.notes,
    },
    contact: {
      ...base.contact,
      ...(legacyKit ? {} : (partial.contact ?? {})),
      eyebrow: legacyKit
        ? base.contact.eyebrow
        : (partial.contact?.eyebrow ?? base.contact.eyebrow),
      heading: legacyKit
        ? base.contact.heading
        : (partial.contact?.heading ?? base.contact.heading),
      headingAccent: legacyKit
        ? base.contact.headingAccent
        : (partial.contact?.headingAccent ?? base.contact.headingAccent),
      body: legacyKit
        ? base.contact.body
        : (partial.contact?.body ?? base.contact.body),
    },
  };

  return formalizeMediaKitTones(merged);
}
